import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { aPIPaths, methods, requestEBankJSON } from 'src/Redux';
import { Notification } from 'src/Components';
import { i18 } from 'src/Utilities';

const getBulkPaymentsFileList = createAsyncThunk('bulkFile/getBulkFileList', async () => {
  try {
    const response = await requestEBankJSON(aPIPaths.get_OMC_BulkPaymentsFileList, {
      method: methods.get,
    });
    return response.data;
  } catch (error: any) {
    return error?.response?.data ?? error;
  }
});

const deleteBulkPaymentsFile = createAsyncThunk('bulkFile/deleteBulkPaymentsFile', async (FileGuid: string) => {
  try {
    const response = await requestEBankJSON(`${aPIPaths.delete_OMC_BulkPaymentsFile}/${FileGuid}`, {
      method: methods.get,
    });
    return response.data;
  } catch (error: any) {
    return error?.response?.data ?? error;
  }
});

const getBulkPaymentsFileDetails = createAsyncThunk('bulkFile/getBulkPaymentsFileDetails', async (fileId: string) => {
  try {
    const response = await requestEBankJSON(`${aPIPaths.get_OMC_BulkPaymentsDetails}/${fileId}`, {
      method: methods.get,
    });
    return response.data;
  } catch (error: any) {
    return error?.response?.data ?? error;
  }
});

const getBulkPaymentsPreview = createAsyncThunk('bulkFile/getBulkPaymentsPreview', async ({ fileGuid, type = '' }: { fileGuid: string; type?: string }) => {
  try {
    const response = await requestEBankJSON(`${aPIPaths.get_PMC_BulkPaymentsPreview}/${fileGuid}?paymentSystem=${type}`, {
      method: methods.get,
    });

    return response.data;
  } catch (error: any) {
    return error?.response?.data ?? error;
  }
});

const postBulkPaymentsSign = createAsyncThunk(
  'bulkFile/postBulkPaymentsSign',
  async ({ FileGuid, Language, PaymentSystem }: { FileGuid: string; Language: string; PaymentSystem: string }) => {
    try {
      const response = await requestEBankJSON(`${aPIPaths.post_OMC_BulkPaymentsSign}`, {
        method: methods.post,
        data: { FileGuid, Language, PaymentSystem },
      });

      return response.data;
    } catch (error: any) {
      return error?.response?.data ?? error;
    }
  }
);

const initialState = {
  FilesList: [],
  FilesListPreview: [],
  FilesListDetails: null,
  FilesListSign: null,
  loading: false,
  error: null,
};

const BulkFileSlice = createSlice({
  name: 'bulkFile',
  initialState,
  reducers: {
    removeFromList(state, action) {
      state.FilesList = state.FilesList.filter((item: any) => item.FileGuid !== action.payload);
    },

    cancelLoading(state) {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBulkPaymentsFileList.pending, (state) => {
      return { ...state, loading: true, error: null };
    });
    builder.addCase(getBulkPaymentsFileList.fulfilled, (state, { payload }) => {
      return { ...state, ...payload, loading: false, error: null };
    });
    builder.addCase(getBulkPaymentsFileList.rejected, (state, action: any) => {
      return { ...state, loading: false, error: action?.error?.message };
    });

    builder.addCase(deleteBulkPaymentsFile.pending, (state) => {
      return { ...state, loading: true, error: null };
    });
    builder.addCase(deleteBulkPaymentsFile.fulfilled, (state, action) => {
      if (action.payload === true) {
        return {
          ...state,
          FilesList: state.FilesList.filter((item: any) => item.FileGuid !== action.meta.arg),
          loading: false,
          error: null,
        };
      }

      return {
        ...state,
        loading: false,
        error: null,
      };
    });
    builder.addCase(deleteBulkPaymentsFile.rejected, (state, action: any) => {
      return { ...state, loading: false, error: action?.error?.message };
    });

    builder.addCase(getBulkPaymentsFileDetails.pending, (state) => {
      return {
        ...state,
        loading: true,
        error: null,
      };
    });
    builder.addCase(getBulkPaymentsFileDetails.fulfilled, (state, action) => {
      return {
        ...state,
        FilesListDetails: action.payload,
        loading: false,
        error: null,
      };
    });
    builder.addCase(getBulkPaymentsFileDetails.rejected, (state, action: any) => {
      return {
        ...state,
        loading: false,
        error: action?.error?.message,
      };
    });

    builder.addCase(getBulkPaymentsPreview.pending, (state) => {
      return {
        ...state,
        loading: true,
        error: null,
        FilesListPreview: [],
      };
    });
    builder.addCase(getBulkPaymentsPreview.fulfilled, (state, { payload }) => {
      if (payload.ErrorCode) {
        payload.PaymentErrorsItems?.forEach((item: any) => {
          Notification({
            type: 'error',
            message: i18.ErrorTitles[payload?.ErrorCode as keyof typeof i18.ErrorTitles],
            description: item?.ErrorDescription,
          });
        });
        return { ...state, loading: false, error: payload.ErrorCode };
      }

      return {
        ...state,
        loading: false,
        error: null,
        FilesListPreview: payload,
      };
    });
    builder.addCase(getBulkPaymentsPreview.rejected, (state, action: any) => {
      return {
        ...state,
        loading: false,
        error: action?.error?.message,
      };
    });

    builder.addCase(postBulkPaymentsSign.pending, (state) => {
      return { ...state, loading: true, error: null };
    });
    builder.addCase(postBulkPaymentsSign.fulfilled, (state, action: any) => {
      if (action.payload.ErrorCode) {
        action.payload.PaymentErrorsItems?.forEach((item: any) => {
          Notification({
            type: 'error',
            message: i18.ErrorTitles[action.payload.ErrorCode as keyof typeof i18.ErrorTitles],
            description: item?.ErrorDescription,
          });
        });
        return { ...state, loading: false, error: action.payload.ErrorCode };
      }

      return {
        ...state,
        FilesListSign: action.payload,
        loading: false,
        error: action.payload.ErrorCode,
      };
    });
    builder.addCase(postBulkPaymentsSign.rejected, (state, action: any) => {
      return { ...state, loading: false, error: action?.error?.message };
    });
  },
});

const BulkFileActions = {
  getBulkPaymentsFileList,
  deleteBulkPaymentsFile,
  getBulkPaymentsFileDetails,
  getBulkPaymentsPreview,
  postBulkPaymentsSign,
  removeFromList: BulkFileSlice.actions.removeFromList,
  cancelLoading: BulkFileSlice.actions.cancelLoading,
};

const { reducer } = BulkFileSlice;
export { reducer as BulkFileReducer, BulkFileActions };
